import { useCallback } from "react";
import type { PublicQuote } from "@forgepoint/shared";
import { QuoteDocumentRenderer } from "@/modules/quoteDocuments/QuoteDocumentRenderer";
import { markBodyRenderComplete } from "./renderReadySignal";

interface PublicQuoteDocumentViewProps {
  /** The public quote carrying a stored, render-valid document. */
  quote: PublicQuote;
  /** The sanitized document HTML to render. */
  documentHtml: string;
}

/**
 * Renders a public quote through the stored HTML document (QuoteDocumentRenderer)
 * instead of a legacy React template. The renderer consumes the customer-safe
 * quote directly and folds the org logo into the server-signed asset map, and
 * this view promotes the renderer's settled signal to `document.body` so the
 * PDF pipeline can wait on a single body-level attribute regardless of which
 * render path produced the page.
 * @param quote - The public quote carrying the document
 * @param documentHtml - The sanitized document HTML
 */
export function PublicQuoteDocumentView({
  quote,
  documentHtml,
}: PublicQuoteDocumentViewProps) {
  const handleSettled = useCallback(() => {
    markBodyRenderComplete();
  }, []);

  return (
    <QuoteDocumentRenderer
      documentHtml={documentHtml}
      quote={quote}
      assetUrls={quote.assetUrls}
      onSettled={handleSettled}
    />
  );
}
